import React from "react";
import {
  Box,
  Typography,
  Icon,
  Button,
  useTheme,
  alpha,
  Skeleton,
  Paper,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";

export interface EmptyStateProps {
  icon: string;
  title: string;
  description: string;
  actionText?: string;
  onAction?: () => void;
  button?: {
    label: string;
    onClick: () => void;
  };
  height?: number | string;
  loading?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionText,
  onAction,
  button,
  height = 400,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          width: "100%",
          p: 2,
          minHeight: 400,
        }}
      >
        {/* Table Header Skeleton */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton
            variant="rectangular"
            width={120}
            height={40}
            sx={{ borderRadius: 1 }}
          />
          <Box sx={{ flex: 1 }} />
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Table Skeleton */}
        <Paper
          elevation={1}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "flex",
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Skeleton
              variant="rectangular"
              width={24}
              height={24}
              sx={{ borderRadius: 0.5, mr: 2 }}
            />
            {[30, 25, 20, 15].map((width, index) => (
              <Skeleton
                key={index}
                variant="text"
                width={`${width}%`}
                height={24}
                sx={{ mr: 2 }}
              />
            ))}
            <Skeleton
              variant="rectangular"
              width={100}
              height={24}
              sx={{ borderRadius: 0.5 }}
            />
          </Box>

          {/* Table Rows */}
          {[1, 2, 3, 4, 5].map((row) => (
            <Box
              key={row}
              sx={{
                display: "flex",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": {
                  borderBottom: "none",
                },
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
                },
              }}
            >
              <Skeleton
                variant="rectangular"
                width={24}
                height={24}
                sx={{ borderRadius: 0.5, mr: 2 }}
              />
              {[30, 25, 20, 15].map((width, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  width={`${width}%`}
                  height={24}
                  sx={{ mr: 2 }}
                />
              ))}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={24}
                  sx={{ borderRadius: 0.5 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={24}
                  sx={{ borderRadius: 0.5 }}
                />
              </Box>
            </Box>
          ))}
        </Paper>

        {/* Table Footer/Pagination Skeleton */}
        <Box
          sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Skeleton
            variant="rectangular"
            width={32}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={32}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={32}
            height={32}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        p: 3,
        textAlign: "center",
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Icon sx={{ fontSize: 40 }}>{icon}</Icon>
        </Box>
      </motion.div>

      <Typography
        variant="h5"
        component={motion.h5}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        gutterBottom
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        component={motion.p}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        sx={{ maxWidth: 400, mb: 4 }}
      >
        {description}
      </Typography>

      {(actionText && onAction) || button ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<Icon>add</Icon>}
            onClick={button ? button.onClick : onAction}
            sx={{
              borderRadius: 2,
              px: 4,
            }}
          >
            {button ? button.label : actionText}
          </Button>
        </motion.div>
      ) : null}
    </Box>
  );
};
